import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CalendarCheck, CalendarPlus, CheckCircle2, Lock, Mail, MapPin, X } from 'lucide-react';
import { Button } from '../Button';
import { downloadIcsContent } from '../../services/calendarService';
import { dateiAusgeben } from '../../services/dateiAusgabe';
import { describeError } from '../../services/errorMessage';
import { istNativeApp } from '../../services/nativePlatform';
import { generateKickoffIcs } from '../../services/kickoff/projectKickoff';
import { benachrichtigeKickoff, kickoffBestaetigen, kickoffSpaeter } from '../../services/kickoff/kickoffService';
import type { KickoffHinweis } from '../../services/kickoff/kickoffTypes';
import {
    alleHakenGesetzt,
    bereichKennzeichen,
    dialogModus,
    dialogTitel,
    eintragFehler,
    fokusZiel,
    hakenSchluessel,
    hakenUmschalten,
    hinweisStufe,
    icsDateiname,
    istAusgeschoepft,
    KENNTNIS_HAKEN,
    KICKOFF_AKTUALISIEREN,
    nichtVerschobenMeldung,
    pflichtHaken,
    plaeneMitTerminen,
    projektIds,
    SIGEPLAN_SELBST_HAKEN,
    spaeterAuswerten,
    terminArtName,
    versandMeldung,
    type DialogMeldung,
    type KickoffDialogEintrag,
    type SpaeterStand,
} from './kickoffDialogLogic';

/**
 * Popup „Neues Projekt zugewiesen“ mit Quittung (docs/projekt-kickoff-plan.md
 * §4.3, Entscheide des Betreibers 11.10.).
 *
 * Ein Block je offenem Projekt; jeder Termin bekommt einen Haken „steht im
 * Kalender“, und erst wenn alle Haken eines Projekts sitzen, lässt es sich
 * bestätigen. Ohne Termine (z. B. Baustart fehlt) steht ein einzelner Haken
 * „Zur Kenntnis genommen“ — auch dann soll niemand das Projekt übersehen.
 * Plan-Hinweise (z. B. SiGe-Plan ohne Samstagsplatz) stehen nur als Text da,
 * sie brauchen keinen Haken.
 *
 * Drei Zustände (`dialogModus`):
 *  - verschiebbar: ×, Esc und „Später (noch n×)“ zählen auf dem Server. Ein
 *    Schließen ohne Zählen wäre ein Weg um die Sperre herum. Ein Klick
 *    daneben schließt bewusst gar nicht: er zählte sonst ein „Später“ mit,
 *    das niemand gemeint hat.
 *  - gesperrt: ein gezeigtes Projekt wurde dreimal verschoben — kein
 *    Ausweg außer Bestätigen.
 *  - Störung: Laden, Bestätigen oder Später ist technisch gescheitert. Dann
 *    lässt sich das Popup schließen, ohne etwas zu verbuchen; beim nächsten
 *    Start kommt es wieder. Eine Sperre, die an einem Netzfehler hängt,
 *    machte die App unbenutzbar.
 *
 * Solange der Dialog offen ist, ist der übrige Seiteninhalt `inert` und der
 * Fokus bleibt im Dialog — sonst wäre die Sperre per Tab und Enter zu
 * umgehen.
 *
 * Der Dialog führt die Aktionen selbst aus; das Gate (ProjectKickoffGate)
 * entscheidet nur, OB er erscheint, und erfährt über die Rückrufe, was
 * passiert ist.
 */
interface Props {
    /** Die gezeigten, unbestätigten Einträge — nie leer. */
    eintraege: KickoffDialogEintrag[];
    /** Angemeldeter Nutzer, Empfänger von „Mail erneut senden“. */
    userId: string;
    /** Technische Störung dieser Sitzung (Gate) oder null. */
    stoerung: string | null;
    /** Projekt bestätigt: Block entfernen. */
    onBestaetigt: (ackId: string) => void;
    /** Antwort von „Später“ — das Gate blendet nur wirklich verschobene Zeilen aus. */
    onVerschoben: (gefragt: string[], staende: SpaeterStand[]) => void;
    /** Bestätigen oder Später ist technisch gescheitert. */
    onStoerung: (grund: string) => void;
    /** Nur im Störungsfall: schließen, ohne etwas zu verbuchen. */
    onSchliessen: (ids: string[]) => void;
}

/** Was per Tab erreichbar ist. Deaktivierte Knöpfe zählen nicht mit. */
const BEDIENBAR =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

const bedienbare = (behaelter: HTMLElement | null): HTMLElement[] =>
    behaelter ? Array.from(behaelter.querySelectorAll<HTMLElement>(BEDIENBAR)) : [];

/** Farben der Hinweise: rot = verpasst/fehlt, orange = beachten. */
const HINWEIS_KLASSE: Record<'rot' | 'orange', string> = {
    rot: 'bg-red-50 text-red-800 border-red-200',
    orange: 'bg-amber-50 text-amber-900 border-amber-200',
};

/** Als span, weil sie auch im Label eines Hakens steht (dort ist kein <p> erlaubt). */
const HinweisZeile: React.FC<{ hinweis: KickoffHinweis }> = ({ hinweis }) => (
    <span className={`flex items-start gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border ${HINWEIS_KLASSE[hinweisStufe(hinweis.art)]}`}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
        <span>{hinweis.text}</span>
    </span>
);

/** Ein Haken in Daumengröße — das Popup wird oft am Handy abgearbeitet. */
const Haken: React.FC<{
    gesetzt: boolean;
    disabled: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}> = ({ gesetzt, disabled, onToggle, children }) => (
    <label className={`flex items-start gap-3 min-h-[44px] px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${gesetzt ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:bg-slate-50'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
        <input
            type="checkbox"
            checked={gesetzt}
            disabled={disabled}
            onChange={onToggle}
            className="mt-0.5 w-5 h-5 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span className="min-w-0 flex-1">{children}</span>
    </label>
);

export const ProjectKickoffDialog: React.FC<Props> = ({
    eintraege, userId, stoerung, onBestaetigt, onVerschoben, onStoerung, onSchliessen,
}) => {
    const titelId = useId();
    const textId = useId();
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [gesetzt, setGesetzt] = useState<Set<string>>(() => new Set());
    const [bestaetigtLaeuft, setBestaetigtLaeuft] = useState<string | null>(null);
    const [blockFehler, setBlockFehler] = useState<Record<string, string>>({});
    const [spaeterLaeuft, setSpaeterLaeuft] = useState(false);
    const [mailLaeuft, setMailLaeuft] = useState(false);
    const [meldung, setMeldung] = useState<DialogMeldung | null>(null);

    const acks = eintraege.map(e => e.ack);
    // Ein nicht ladbares Projekt zählt wie eine Störung (dialogModus).
    const modus = dialogModus(eintraege, stoerung);
    const gesperrt = modus.art === 'gesperrt';
    const kalenderPlaene = plaeneMitTerminen(eintraege);
    const beschaeftigt = spaeterLaeuft || bestaetigtLaeuft !== null;

    const spaeter = async () => {
        // Doppelte Sicherung zur Oberfläche: ohne Knopf darf auch Esc nicht zählen.
        if (modus.art !== 'verschiebbar' || beschaeftigt) return;
        const ids = acks.map(a => a.id);
        setSpaeterLaeuft(true);
        setMeldung(null);
        try {
            const staende: SpaeterStand[] = await kickoffSpaeter(ids);
            // Hat ein zweites Gerät schneller verschoben, bleibt die Zeile
            // stehen (Stand 3 sperrt sofort) — das braucht ein Wort dazu.
            const { nichtVerschoben } = spaeterAuswerten(eintraege, ids, staende);
            if (nichtVerschoben.length > 0) setMeldung({ art: 'fehler', text: nichtVerschobenMeldung(nichtVerschoben.length) });
            onVerschoben(ids, staende);
        } catch (e) {
            // Die RPC antwortet fachlich nie mit einem Fehler — das hier ist Technik.
            onStoerung(describeError(e));
        } finally {
            setSpaeterLaeuft(false);
        }
    };

    const schliessen = () => {
        if (modus.art !== 'stoerung' || beschaeftigt) return;
        onSchliessen(acks.map(a => a.id));
    };

    /** × und Esc: je nach Zustand „Später“, Schließen ohne Zählen oder nichts. */
    const wegKlick = () => {
        if (modus.art === 'verschiebbar') void spaeter();
        else if (modus.art === 'stoerung') schliessen();
    };

    // Über einen Ref, damit der Tastatur-Listener nicht bei jedem Haken neu
    // angemeldet wird und trotzdem den aktuellen Zustand sieht.
    const wegKlickRef = useRef(wegKlick);
    useEffect(() => { wegKlickRef.current = wegKlick; });

    // Fokusfalle und Esc. Tab und Umschalt+Tab laufen im Kreis durch die
    // Bedienelemente des Dialogs; liegt der Fokus draußen, wird er
    // hereingeholt.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Tab') {
                const liste = bedienbare(dialogRef.current);
                const aktiv = document.activeElement;
                const index = aktiv instanceof HTMLElement ? liste.indexOf(aktiv) : -1;
                const ziel = fokusZiel(liste.length, index, e.shiftKey);
                if (ziel !== null) {
                    e.preventDefault();
                    liste[ziel].focus();
                } else if (liste.length === 0) {
                    e.preventDefault();
                }
                return;
            }
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            e.preventDefault();
            wegKlickRef.current();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // Übriger Seiteninhalt inert, solange der Dialog offen ist: das Overlay
    // sperrt nur die Maus, per Tab und Enter blieb die App dahinter
    // bedienbar. Das Portal hängt direkt an <body>; inert werden alle seine
    // Geschwister (u. a. #root) — aber nur die, die es nicht schon waren, damit
    // das Aufräumen keinen fremden Zustand zurücknimmt. Beim Schließen kehrt
    // der Fokus dorthin zurück, wo er vorher war.
    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;
        const vorherFokus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const inertGesetzt: Element[] = [];
        for (const el of Array.from(document.body.children)) {
            if (el === overlay || el.hasAttribute('inert')) continue;
            el.setAttribute('inert', '');
            inertGesetzt.push(el);
        }
        // Start auf dem ersten Bedienelement — außer dem ×: das zählt als
        // „Später“, und ein Enter aus Versehen soll nicht verschieben.
        const start = bedienbare(dialogRef.current).find(el => !el.hasAttribute('data-kein-startfokus'));
        start?.focus();
        return () => {
            for (const el of inertGesetzt) el.removeAttribute('inert');
            if (vorherFokus && document.contains(vorherFokus)) vorherFokus.focus();
        };
    }, []);

    const bestaetigen = async (eintrag: KickoffDialogEintrag) => {
        const id = eintrag.ack.id;
        if (!alleHakenGesetzt(eintrag, gesetzt) || beschaeftigt) return;
        setBestaetigtLaeuft(id);
        setBlockFehler(prev => { const uebrig = { ...prev }; delete uebrig[id]; return uebrig; });
        try {
            await kickoffBestaetigen(id);
            onBestaetigt(id);
        } catch (e) {
            setBlockFehler(prev => ({ ...prev, [id]: `Bestätigen hat nicht geklappt: ${describeError(e)}` }));
            // Technischer Fehler: die Sperre darf daran nicht hängen bleiben.
            onStoerung(describeError(e));
        } finally {
            setBestaetigtLaeuft(null);
        }
    };

    const kalenderLaden = async () => {
        if (kalenderPlaene.length === 0) return;
        setMeldung(null);
        try {
            const ics = generateKickoffIcs(kalenderPlaene);
            const name = icsDateiname(kalenderPlaene, new Date());
            // In der Android-App kommt ein Blob-Download nie an (dateiAusgabe.ts);
            // dort öffnet sich der Teilen-Dialog. Der Browser behält den
            // gewohnten Download.
            if (istNativeApp()) await dateiAusgeben(new Blob([ics], { type: 'text/calendar;charset=utf-8' }), name);
            else downloadIcsContent(ics, name);
            setMeldung({ art: 'ok', text: `Kalenderdatei ${name} erstellt – öffnen, Termine übernehmen und hier abhaken.` });
        } catch (e) {
            setMeldung({ art: 'fehler', text: `Kalenderdatei nicht erstellt: ${describeError(e)}` });
        }
    };

    const mailErneut = async () => {
        if (mailLaeuft) return;
        setMailLaeuft(true);
        setMeldung(null);
        try {
            const ergebnisse = await benachrichtigeKickoff(projektIds(eintraege), { erneut: true, nurEmpfaenger: userId });
            setMeldung(versandMeldung(ergebnisse));
        } catch (e) {
            // benachrichtigeKickoff wirft laut Vertrag nie — der Gürtel bleibt trotzdem.
            setMeldung({ art: 'fehler', text: `Mail nicht gesendet: ${describeError(e)}` });
        } finally {
            setMailLaeuft(false);
            // Der Versand vergibt einen fehlenden Samstagsplatz neu — frisch
            // laden, damit die SiGe-Plan-Serie im Popup erscheint.
            window.dispatchEvent(new Event(KICKOFF_AKTUALISIEREN));
        }
    };

    const umschalten = (schluessel: string) => setGesetzt(prev => hakenUmschalten(prev, schluessel));

    const wegLabel = modus.art === 'verschiebbar' ? `Später (noch ${modus.rest}×)` : 'Schließen';

    return createPortal((
        // z-[500]: über allem, auch über der Tour (z-[400]). Der Rest der
        // Seite ist ohnehin inert, solange der Dialog offen ist — ein Fenster,
        // das darüber läge, wäre sichtbar, aber nicht bedienbar.
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[500] bg-slate-900/50 flex items-stretch sm:items-center justify-center sm:p-4 animate-fade-in"
            style={{ height: '100dvh' }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titelId}
                aria-describedby={textId}
                className="bg-white w-full h-full sm:h-auto sm:max-w-2xl sm:rounded-2xl shadow-2xl sm:border border-slate-200 flex flex-col overflow-hidden"
                style={{ maxHeight: '100dvh' }}
            >
                {/* Kopf */}
                <div className={`px-5 pb-4 shrink-0 border-b ${gesperrt ? 'bg-red-50 border-red-100' : 'border-slate-100'}`} style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className={`p-2 rounded-xl border shrink-0 ${gesperrt ? 'bg-red-100 text-red-700 border-red-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                {gesperrt ? <Lock className="w-5 h-5" /> : <CalendarCheck className="w-5 h-5" />}
                            </div>
                            <div className="min-w-0">
                                <h2 id={titelId} className="font-semibold text-slate-800 leading-tight">{dialogTitel(eintraege.length, gesperrt)}</h2>
                                <p id={textId} className="text-xs text-slate-600 mt-1 leading-relaxed">
                                    {gesperrt
                                        ? 'Die Bestätigung wurde dreimal verschoben. Die App ist gesperrt, bis die markierten Projekte bestätigt sind.'
                                        : 'Bitte die Termine in Ihren Kalender übernehmen (Kalenderdatei oder Mail), jeden Termin abhaken und das Projekt bestätigen.'}
                                </p>
                            </div>
                        </div>
                        {modus.art !== 'gesperrt' && (
                            <button
                                type="button"
                                onClick={wegKlick}
                                disabled={beschaeftigt}
                                aria-label={wegLabel}
                                title={wegLabel}
                                data-kein-startfokus=""
                                className="p-2 -mr-1 -mt-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-40 shrink-0"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                    {modus.art === 'stoerung' && (
                        <p role="alert" className="mt-3 flex items-start gap-2 text-xs font-medium text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
                            <span>{modus.hinweis}</span>
                        </p>
                    )}
                </div>

                {/* Projekte */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-5 py-4 space-y-4 bg-slate-50/60">
                    {eintraege.map(eintrag => {
                        const { ack, plan } = eintrag;
                        const bereich = bereichKennzeichen(ack.bereich);
                        const termine = plan?.termine ?? [];
                        const fehler = eintragFehler(eintrag);
                        const pflicht = pflichtHaken(eintrag);
                        const kenntnisSchluessel = hakenSchluessel(ack.id, KENNTNIS_HAKEN);
                        const sigeplanSchluessel = hakenSchluessel(ack.id, SIGEPLAN_SELBST_HAKEN);
                        const fertig = alleHakenGesetzt(eintrag, gesetzt);
                        const laeuft = bestaetigtLaeuft === ack.id;
                        const ausgeschoepft = istAusgeschoepft(ack);
                        const kopf = [plan?.projectNumber, plan?.projectName].filter(Boolean).join(' · ') || 'Unbekanntes Projekt';
                        return (
                            <section
                                key={ack.id}
                                aria-label={kopf}
                                className={`bg-white rounded-2xl border shadow-xs p-4 space-y-3 ${ausgeschoepft ? 'border-red-300 ring-1 ring-red-200' : 'border-slate-200'}`}
                            >
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-slate-800 break-words">{kopf}</h3>
                                        {plan?.projectAddress && (
                                            <p className="text-xs text-slate-600 mt-0.5 flex items-start gap-1 break-words">
                                                <MapPin className="w-3 h-3 shrink-0 mt-0.5" aria-hidden="true" />
                                                <span>{plan.projectAddress}</span>
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        {bereich && (
                                            <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{bereich}</span>
                                        )}
                                        {ausgeschoepft && (
                                            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">3× verschoben</span>
                                        )}
                                    </div>
                                </div>

                                {/*
                                  * Nicht ladbares Projekt: kein Haken, kein Bestätigen. Ein
                                  * „Zur Kenntnis genommen“ quittierte sonst ein Projekt, das
                                  * niemand gesehen hat. Die Zeile bleibt offen.
                                  */}
                                {fehler !== null ? (
                                    <p className="flex items-start gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border bg-red-50 text-red-800 border-red-200">
                                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                                        <span>Projekt konnte nicht geladen werden ({fehler}). Es bleibt offen und erscheint beim nächsten Start wieder.</span>
                                    </p>
                                ) : (<>
                                    {plan && plan.hinweise.length > 0 && (
                                        <div className="space-y-1.5">
                                            {plan.hinweise.map((h, i) => <HinweisZeile key={`${h.art}-${i}`} hinweis={h} />)}
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        {termine.map(t => {
                                            const schluessel = hakenSchluessel(ack.id, t.uid);
                                            return (
                                                <Haken key={t.uid} gesetzt={gesetzt.has(schluessel)} disabled={laeuft} onToggle={() => umschalten(schluessel)}>
                                                    <span className="block text-sm font-medium text-slate-800">
                                                        {terminArtName(t.art)} <span className="text-slate-600 font-normal">· {t.anzeige}</span>
                                                    </span>
                                                    <span className="block text-[11px] text-slate-500 mt-0.5">steht im Kalender</span>
                                                    {t.hinweise.length > 0 && (
                                                        <span className="block mt-1.5 space-y-1">
                                                            {t.hinweise.map((h, i) => <HinweisZeile key={`${h.art}-${i}`} hinweis={h} />)}
                                                        </span>
                                                    )}
                                                </Haken>
                                            );
                                        })}
                                        {pflicht.includes(sigeplanSchluessel) && (
                                            // Die Serie fehlt in der Kalenderdatei (kein Samstagsplatz).
                                            // „Mail erneut senden“ versucht die Platzvergabe neu; sonst
                                            // bestätigt der Nutzer ausdrücklich den eigenen Eintrag.
                                            <div className="space-y-1.5">
                                                <Haken gesetzt={gesetzt.has(sigeplanSchluessel)} disabled={laeuft} onToggle={() => umschalten(sigeplanSchluessel)}>
                                                    <span className="block text-sm font-medium text-slate-800">SiGe-Plan-Termin (jeden Samstag, 30 min) selbst im Kalender eingetragen</span>
                                                    <span className="block text-[11px] text-slate-500 mt-0.5">Die Serie fehlt noch in der Kalenderdatei.</span>
                                                </Haken>
                                                <div className="flex justify-end">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => { void mailErneut(); }}
                                                        loading={mailLaeuft}
                                                        icon={<Mail className="w-3.5 h-3.5" />}
                                                        className="min-h-[36px]"
                                                    >
                                                        Mail erneut senden
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {pflicht.includes(kenntnisSchluessel) && (
                                            <Haken gesetzt={gesetzt.has(kenntnisSchluessel)} disabled={laeuft} onToggle={() => umschalten(kenntnisSchluessel)}>
                                                <span className="block text-sm font-medium text-slate-800">Zur Kenntnis genommen</span>
                                                <span className="block text-[11px] text-slate-500 mt-0.5">Für dieses Projekt gibt es noch keine Kalendertermine.</span>
                                            </Haken>
                                        )}
                                    </div>
                                </>)}

                                {blockFehler[ack.id] && (
                                    <p role="alert" className="text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">{blockFehler[ack.id]}</p>
                                )}

                                {fehler === null && (
                                    <div className="flex justify-end">
                                        <Button
                                            variant="primary"
                                            onClick={() => { void bestaetigen(eintrag); }}
                                            disabled={!fertig || (beschaeftigt && !laeuft)}
                                            loading={laeuft}
                                            icon={<CheckCircle2 className="w-4 h-4" />}
                                            className="min-h-[44px] w-full sm:w-auto"
                                            title={fertig ? undefined : 'Erst alle Haken setzen'}
                                        >
                                            Bestätigen
                                        </Button>
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                {/* Fuß */}
                <div className="shrink-0 border-t border-slate-200 bg-white px-4 sm:px-5 pt-3 space-y-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
                    {meldung && (
                        <p
                            role={meldung.art === 'fehler' ? 'alert' : 'status'}
                            className={`text-xs font-medium rounded-lg px-3 py-2 border ${meldung.art === 'fehler' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}
                        >
                            {meldung.text}
                        </p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <Button
                            variant="secondary"
                            onClick={() => { void kalenderLaden(); }}
                            disabled={kalenderPlaene.length === 0}
                            icon={<CalendarPlus className="w-4 h-4" />}
                            className="min-h-[44px]"
                            title={kalenderPlaene.length === 0 ? 'Keine Termine für den Kalender' : undefined}
                        >
                            Kalenderdatei herunterladen
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => { void mailErneut(); }}
                            loading={mailLaeuft}
                            icon={<Mail className="w-4 h-4" />}
                            className="min-h-[44px]"
                        >
                            Mail erneut senden
                        </Button>
                        {modus.art === 'verschiebbar' && (
                            <Button
                                variant="ghost"
                                onClick={() => { void spaeter(); }}
                                disabled={beschaeftigt}
                                loading={spaeterLaeuft}
                                className="min-h-[44px] sm:ml-auto"
                            >
                                Später (noch {modus.rest}×)
                            </Button>
                        )}
                        {modus.art === 'stoerung' && (
                            <Button
                                variant="ghost"
                                onClick={schliessen}
                                disabled={beschaeftigt}
                                className="min-h-[44px] sm:ml-auto"
                            >
                                Schließen
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
};
